<template>
  <BuilderWrapper
    popout
    title="Bar Chart"
    helpful-tip="The comparison graph is an optional feature that shows a bar graph in the left hand side panel to compare two values per category."
    :show-premium-feature="!featureEnabled"
    :modal-disabled="!featureEnabled"
    ok-only
    @show="resetSettings"
    @ok="saveSettings"
  >
    <template v-if="featureEnabled">
      <b-row>
        <b-col>
          <LabelledInput>
            <b-checkbox v-model="showGraph">
              Show Comparison Bar Graph
            </b-checkbox>
          </LabelledInput>

          <b-form
            v-if="showGraph && !exclude.includes('settings')"
            class="px-1"
          >
            <LabelledInput
              v-if="hasMultipleUniqueUnits"
              label="Bar chart unit"
              helpful-tip="Select the unit for the chart. Only fields with a matching unit will be shown."
              class="pb-1"
            >
              <b-select
                v-model="barUnit"
                :options="fieldUnitOptions"
              />
            </LabelledInput>
            <b-row
              class="flex-grow-1"
              no-gutters
            >
              <b-input-group class="justify-content-end">
                <AddLegendItem
                  :categories="categories"
                  :legenditems="legenditems"
                  :v="$v"
                />
                <LegendLabels
                  :categories="categories"
                  :legenditems="legenditems"
                  :v="$v"
                />
              </b-input-group>
            </b-row>
            <b-row
              v-for="(_, row) in categories"
              :key="`category-${row}`"
              class="flex-grow-1"
              no-gutters
            >
              <CategoryTitles
                :categories="categories"
                :title.sync="categories[row].title"
                :row="row"
                :any-error="$v.categories.$each[row].title.$anyError"
                :v="$v"
              />
              <CategoryRows
                :fields="categories[row].fields"
                :row="row"
                :v="$v.categories.$each[row].fields"
                :chart-unit="barUnit"
              />
            </b-row>

            <b-row
              class="flex-grow-1"
              no-gutters
            >
              <b-input-group class="justify-content-between">
                <AddCategory
                  :categories="categories"
                  :legenditems="legenditems"
                  :v="$v"
                />
                <LegendColors :legenditems="legenditems" />
              </b-input-group>
            </b-row>

            <GraphSettings
              :rotate-graph.sync="rotateGraph"
              :show-x-axis.sync="showXAxis"
              :show-y-axis.sync="showYAxis"
              :show-legend.sync="showLegend"
            />
          </b-form>
        </b-col>
        <b-col
          v-if="showGraph && !exclude.includes('settings')"
          cols="4"
        >
          <PreviewComparisonGraph
            :preview-rotate-graph="rotateGraph"
            :preview-show-x-axis="showXAxis"
            :preview-show-y-axis="showYAxis"
            :preview-show-legend="showLegend"
            :preview-categories="categories"
            :preview-legenditems="legenditems"
          />
        </b-col>
      </b-row>
    </template>
  </BuilderWrapper>
</template>

<script lang="ts">

import { PropType } from 'vue'
import Component from 'vue-class-component'

import FeatureSetMixin from '@/mixins/FeatureSetMixin'
import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import LegendLabels from '@/components/builder/ComparisonGraphSettings/LegendLabels.vue'
import CategoryTitles from '@/components/builder/ComparisonGraphSettings/CategoryTitles.vue'
import CategoryRows from '@/components/builder/ComparisonGraphSettings/CategoryRows.vue'
import AddCategory from '@/components/builder/ComparisonGraphSettings/AddCategory.vue'
import AddLegendItem from '@/components/builder/ComparisonGraphSettings/AddLegendItem.vue'
import LegendColors from '@/components/builder/ComparisonGraphSettings/LegendColors.vue'
import GraphSettings from '@/components/builder/ComparisonGraphSettings/GraphSettings.vue'
import PreviewComparisonGraph from '@/components/builder/ComparisonGraphSettings/PreviewComparisonGraph.vue'
import { required } from 'vuelidate/lib/validators'

import {
  ComparisonGraphCategory,
  ComparisonGraphLegendItem,
  FeatureKeys,
  Feature,
  FieldTypes,
  isFieldMonetary,
} from '@/types'
import {
  COMPARISON_GRAPH_CONFIG_KEY,
  UNIT_TYPE_MONETARY,
} from '@/constants'
const cloneDeep = require('lodash.clonedeep')

const components = {
  BuilderWrapper,
  LabelledInput,
  LegendLabels,
  CategoryTitles,
  CategoryRows,
  AddCategory,
  AddLegendItem,
  LegendColors,
  GraphSettings,
  PreviewComparisonGraph,
}

const props = {
  exclude: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
}

// eslint-disable-next-line no-use-before-define
@Component<ComparisonGraphSettings>({
  components,
  watch: {
    settings () {
      this.resetSettings()
    },
  },
})
export default class ComparisonGraphSettings extends FeatureSetMixin.extend({ props }) {
  rotateGraph: boolean = false
  showXAxis: boolean = true
  showYAxis: boolean = true
  showLegend: boolean = true
  legenditems: ComparisonGraphLegendItem[] = []
  categories: ComparisonGraphCategory[] = []
  chartUnit: string | null = null

  featureKey: Feature = FeatureKeys.BAR_CHART
  featureEnabled!: boolean

  validations () {
    return {
      categories: {
        $each: {
          title: {
            required,
            unique: (val: string) => {
              return this.categoryTitles.filter((title: string) => title === val).length === 1
            },
          },
          fields: {
            $each: {
              required,
            },
          },
        },
      },
      legenditems: {
        $each: {
          label: {
            required,
            unique: (val: string) => {
              return this.legendItemLabels.filter((label: string) => label === val).length === 1
            },
          },
        },
      },
    }
  }

  get showGraph () {
    return this.$typedStore.primary.app.config[COMPARISON_GRAPH_CONFIG_KEY].show
  }

  set showGraph (value: boolean) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [COMPARISON_GRAPH_CONFIG_KEY, 'show'],
      value,
    })
  }

  get fields () {
    return this.categories.reduce((acc: string[], { fields }) => acc.concat(fields), [])
  }

  get barUnit () {
    if (this.chartUnit === null) {
      if (this.fields.length > 0) {
        // Set the chartUnit to unit of the already selected fields
        const firstField = this.fieldMap.find(({ column }) => column === this.fields[0])
        if (firstField?.type === FieldTypes.MONETARY) {
          return UNIT_TYPE_MONETARY
        } else {
          return firstField?.unit || this.fieldUnitOptions[0].value
        }
      } else {
        return this.fieldUnitOptions[0].value
      }
    }
    return this.chartUnit
  }

  set barUnit (newValue) {
    this.chartUnit = newValue
    // Remove old chart items
    this.categories.forEach((category) => {
      category.fields = Array(this.legenditems.length).fill('')
    })
  }

  get fieldMap () {
    return this.$typedStore.primary.app.config.fieldMap
  }

  get hasMonetaryUnit () {
    return this.fieldMap.some(isFieldMonetary)
  }

  get uniqueNumericUnits () {
    return this.$typedStore.visualisationApp.uniqueNumericUnits
  }

  get hasMultipleUniqueUnits () {
    const numberOfUniqueNumericUnits = this.uniqueNumericUnits.length
    const numerOfMonetaryUnits = this.hasMonetaryUnit ? 1 : 0
    return numberOfUniqueNumericUnits + numerOfMonetaryUnits > 1
  }

  get fieldUnitOptions () {
    return [
      ...this.hasMonetaryUnit ? [{ value: UNIT_TYPE_MONETARY, text: '<Monetary>' }] : [],
      ...this.uniqueNumericUnits.map((unit) => ({
        value: unit,
        text: unit === '' ? '<No unit>' : unit,
      })),
    ]
  }

  get settings () {
    return this.$typedStore.primary.app.config[COMPARISON_GRAPH_CONFIG_KEY]
  }

  get categoryTitles () {
    return this.categories.map(({ title }) => title)
  }

  get legendItemLabels () {
    return this.legenditems.map(({ label }) => label)
  }

  resetSettings () {
    const settings = cloneDeep(this.settings)
    this.rotateGraph = settings.rotate
    this.showXAxis = settings.showXAxis
    this.showYAxis = settings.showYAxis
    this.showLegend = settings.showLegend
    this.categories = settings.categories
    this.legenditems = settings.legenditems
    this.$v.$touch()
  }

  saveSettings () {
    const payload = {
      configPath: [COMPARISON_GRAPH_CONFIG_KEY],
      value: {
        show: this.showGraph,
        rotate: this.rotateGraph,
        showXAxis: this.showXAxis,
        showYAxis: this.showYAxis,
        showLegend: this.showLegend,
        categories: this.categories,
        legenditems: this.legenditems,
      },
    }
    this.$typedStore.primary.app.updateConfig(payload)
  }
}

</script>
